/* Cart logic — hardened version (safe if progress bar not present) */

/* ===== Helpers from theme (already on window via global.js) =====
   - debounce
   - fetchConfig
   - routes
   - PUB_SUB_EVENTS
   Do NOT redefine them here.
*/

class CartRemoveButton extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('click', (event) => {
      event.preventDefault();
      const cartItems = this.closest('cart-items') || this.closest('cart-drawer-items');
      if (!cartItems) return;
      cartItems.updateQuantity(this.dataset.index, 0);
    });
  }
}
customElements.define('cart-remove-button', CartRemoveButton);

class CartItems extends HTMLElement {
  constructor() {
    super();

    this.lineItemStatusElement =
      document.getElementById('shopping-cart-line-item-status') ||
      document.getElementById('CartDrawer-LineItemStatus');

    const debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, ON_CHANGE_DEBOUNCE_TIMER);

    this.addEventListener('change', debouncedOnChange.bind(this));
  }

  cartUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source === 'cart-items') return;
      this.onCartUpdate();
    });
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) this.cartUpdateUnsubscriber();
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`) || this.querySelector(`#Drawer-quantity-${id}`);
    if (!input) return;
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;
    let message = '';

    if (inputValue < event.target.dataset.min) {
      message = window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min);
    } else if (event.target.max && inputValue > parseInt(event.target.max)) {
      message = window.quickOrderListStrings.max_error.replace('[max]', event.target.max);
    } else if (parseInt(event.target.step) > 0 && inputValue % parseInt(event.target.step) !== 0) {
      message = window.quickOrderListStrings.step_error.replace('[step]', event.target.step);
    }

    if (message) {
      this.setValidity(event, index, message);
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      this.updateQuantity(
        index,
        inputValue,
        document.activeElement && document.activeElement.getAttribute('name'),
        event.target.dataset.quantityVariantId
      );
    }
  }

  onChange(event) {
    this.validateQuantity(event);
  }

  onCartUpdate() {
    if (this.tagName === 'CART-DRAWER-ITEMS') {
      fetch(`${routes.cart_url}?section_id=cart-drawer`)
        .then((r) => r.text())
        .then((htmlText) => {
          const html = new DOMParser().parseFromString(htmlText, 'text/html');
          const selectors = ['cart-drawer-items', '.cart-drawer__footer'];
          for (const selector of selectors) {
            const target = document.querySelector(selector);
            const source = html.querySelector(selector);
            if (target && source) target.replaceWith(source);
          }
        })
        .catch(console.error);
    } else {
      fetch(`${routes.cart_url}?section_id=main-cart-items`)
        .then((r) => r.text())
        .then((htmlText) => {
          const html = new DOMParser().parseFromString(htmlText, 'text/html');
          const sourceQty = html.querySelector('cart-items');
          if (sourceQty) this.innerHTML = sourceQty.innerHTML;
        })
        .catch(console.error);
    }
  }

  getSectionsToRender() {
    const sections = [];

    const mainItems = document.getElementById('main-cart-items');
    if (mainItems) {
      sections.push({
        id: 'main-cart-items',
        section: mainItems.dataset.id,
        selector: '.js-contents',
      });
    }

    sections.push({
      id: 'cart-icon-bubble',
      section: 'cart-icon-bubble',
      selector: '.shopify-section',
    });

    const liveRegion = document.getElementById('cart-live-region-text');
    if (liveRegion) {
      sections.push({
        id: 'cart-live-region-text',
        section: 'cart-live-region-text',
        selector: '.shopify-section',
      });
    }

    const footer = document.getElementById('main-cart-footer');
    if (footer) {
      sections.push({
        id: 'main-cart-footer',
        section: footer.dataset.id,
        selector: '.js-contents',
      });
    }

    return sections;
  }

  updateQuantity(line, quantity, name, variantId) {
    this.enableLoading(line);

    const body = JSON.stringify({
      line,
      quantity,
      sections: this.getSectionsToRender().map((s) => s.section),
      sections_url: window.location.pathname,
    });

    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
      .then((r) => r.text())
      .then((stateText) => {
        const parsedState = JSON.parse(stateText);

        const quantityElement =
          document.getElementById(`Quantity-${line}`) || document.getElementById(`Drawer-quantity-${line}`);
        const items = document.querySelectorAll('.cart-item');

        if (parsedState.errors) {
          if (quantityElement) quantityElement.value = quantityElement.getAttribute('value');
          this.updateLiveRegions(line, parsedState.errors);
          return;
        }

        this.classList.toggle('is-empty', parsedState.item_count === 0);

        const cartDrawerWrapper = document.querySelector('cart-drawer');
        const cartFooter = document.getElementById('main-cart-footer');
        if (cartFooter) cartFooter.classList.toggle('is-empty', parsedState.item_count === 0);
        if (cartDrawerWrapper) cartDrawerWrapper.classList.toggle('is-empty', parsedState.item_count === 0);

        this.getSectionsToRender().forEach((section) => {
          const el =
            document.getElementById(section.id).querySelector(section.selector) ||
            document.getElementById(section.id);
          if (!el) return;
          el.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.section], section.selector);
        });

        const updatedValue = parsedState.items[line - 1] ? parsedState.items[line - 1].quantity : undefined;
        let message = '';
        if (items.length === parsedState.items.length && quantityElement && updatedValue !== parseInt(quantityElement.value)) {
          message = typeof updatedValue === 'undefined'
            ? window.cartStrings.error
            : window.cartStrings.quantityError.replace('[quantity]', updatedValue);
        }
        this.updateLiveRegions(line, message);

        const lineItem =
          document.getElementById(`CartItem-${line}`) || document.getElementById(`CartDrawer-Item-${line}`);
        if (lineItem && name && lineItem.querySelector(`[name="${name}"]`)) {
          cartDrawerWrapper
            ? trapFocus(cartDrawerWrapper, lineItem.querySelector(`[name="${name}"]`))
            : lineItem.querySelector(`[name="${name}"]`).focus();
        } else if (parsedState.item_count === 0 && cartDrawerWrapper) {
          trapFocus(cartDrawerWrapper.querySelector('.drawer__inner-empty'), cartDrawerWrapper.querySelector('a'));
        } else if (document.querySelector('.cart-item') && cartDrawerWrapper) {
          trapFocus(cartDrawerWrapper, document.querySelector('.cart-item__name'));
        }

        // --- SAFE progress bar update (no crash if missing) ---
        try {
          this.updateProgressBar(parsedState.total_price || 0, parsedState.item_count || 0);
        } catch (e) {
          console.debug('Progress bar skipped:', e);
        }

        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: parsedState, variantId });
      })
      .catch(() => {
        this.querySelectorAll('.loading__spinner').forEach((o) => o.classList.add('hidden'));
        const errors = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
        if (errors) errors.textContent = window.cartStrings.error;
      })
      .finally(() => {
        this.disableLoading(line);
      });
  }

  /* ===== Progress bar — completely optional ===== */
  updateProgressBar(cartTotal, itemCount) {
    // If your theme doesn’t have the wrapper, just return quietly.
    const progressWrapper = document.getElementById('cart-progress-wrapper');
    if (!progressWrapper) return;

    const progressBar = document.getElementById('cart-progress-bar');
    const goalMessageElement = document.querySelector('.goal-message');

    // Read dataset safely; fall back if missing.
    const currencyFormat = progressWrapper.dataset.currencyFormat || '{{amount}}';
    const threshold = parseInt(progressWrapper.dataset.threshold || '0', 10);
    const preGoalTpl = progressWrapper.dataset.preGoalMessageTemplate || 'Spend [remaining_for_goal] to reach the goal.';
    const postGoalMsg = progressWrapper.dataset.postGoalMessage || 'Goal reached!';

    if (!progressBar || !threshold) return;

    if (!itemCount || !cartTotal) {
      progressWrapper.style.display = 'none';
      if (goalMessageElement) goalMessageElement.style.display = 'none';
      return;
    }

    progressWrapper.style.display = 'block';
    progressBar.style.display = 'block';

    const pct = Math.min((cartTotal / threshold) * 100, 100);
    progressBar.style.width = `${pct}%`;
    progressWrapper.classList.toggle('full', pct >= 100);

    if (goalMessageElement) {
      goalMessageElement.style.display = 'block';
      let remaining = threshold - cartTotal;
      if (remaining < 0) remaining = 0;

      const remainingDollars = remaining / 100; // Shopify totals are in cents
      const remainingFormatted = this.formatCurrency(currencyFormat, remainingDollars);
      const msg = remaining > 0 ? preGoalTpl.replace('[remaining_for_goal]', remainingFormatted) : postGoalMsg;
      goalMessageElement.innerHTML = msg;
    }
  }

  formatCurrency(currencyFormat, amount) {
    try {
      // Very basic formatter; respects a few Shopify format tokens if passed from DOM
      let out = currencyFormat;
      out = out.replace('{{amount}}', amount.toFixed(2));
      out = out.replace('{{amount_no_decimals}}', amount.toFixed(0));
      out = out.replace('{{amount_with_comma_separator}}', amount.toFixed(2).replace('.', ','));
      out = out.replace('{{amount_no_decimals_with_comma_separator}}', amount.toFixed(0).replace('.', ','));
      out = out.replace('{{amount_with_apostrophe_separator}}', amount.toFixed(2).replace('.', "'"));
      // Fallback if nothing matched
      if (out === currencyFormat) out = `$${amount.toFixed(2)}`;
      return out;
    } catch (e) {
      return `$${amount.toFixed(2)}`;
    }
  }

  updateLiveRegions(line, message) {
    const lineItemError =
      document.getElementById(`Line-item-error-${line}`) ||
      document.getElementById(`CartDrawer-LineItemError-${line}`);
    if (lineItemError) lineItemError.querySelector('.cart-item__error-text').textContent = message;

    if (this.lineItemStatusElement) this.lineItemStatusElement.setAttribute('aria-hidden', true);

    const cartStatus =
      document.getElementById('cart-live-region-text') || document.getElementById('CartDrawer-LiveRegionText');
    if (!cartStatus) return;

    cartStatus.setAttribute('aria-hidden', false);
    setTimeout(() => {
      cartStatus.setAttribute('aria-hidden', true);
    }, 1000);
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  enableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    if (mainCartItems) mainCartItems.classList.add('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);
    [...cartItemElements, ...cartDrawerItemElements].forEach((o) => o.classList.remove('hidden'));

    if (this.lineItemStatusElement) this.lineItemStatusElement.setAttribute('aria-hidden', false);
    if (document.activeElement) document.activeElement.blur();
  }

  disableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    if (mainCartItems) mainCartItems.classList.remove('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);
    cartItemElements.forEach((o) => o.classList.add('hidden'));
    cartDrawerItemElements.forEach((o) => o.classList.add('hidden'));
  }
}
customElements.define('cart-items', CartItems);

/* Cart note (unchanged) */
if (!customElements.get('cart-note')) {
  customElements.define(
    'cart-note',
    class CartNote extends HTMLElement {
      constructor() {
        super();
        this.addEventListener(
          'input',
          debounce((event) => {
            const body = JSON.stringify({ note: event.target.value });
            fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } });
          }, ON_CHANGE_DEBOUNCE_TIMER)
        );
      }
    }
  );
}
